import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/api'; // Importamos el cliente API
import MediaPlaceholder from '../common/MediaPlaceholder';
import { buildWhatsappUrl } from '../../utils/whatsapp';

const AdCard = ({ ad, fallbackAssets = [], badgeLabel = '' }) => {
  const adRef = useRef(null);
  const navigate = useNavigate();

  // Si no hay datos del anuncio, no renderizar nada.
  if (!ad) {
    return null;
  }

  // Función para registrar un evento
  const trackEvent = async (eventType) => {
    // 🟢 SOLUCIÓN: Solo registrar el evento si el anuncio ya tiene un ID (no es una vista previa)
    if (!ad._id) return;
    try {
      await apiClient.post(`/ads/${ad._id}/track`, { eventType });
    } catch (error) {
      // El error se maneja silenciosamente para no afectar al usuario.
      console.error(`Error al registrar ${eventType}:`, error);
    }
  };

  const resolveTarget = () => {
    const actionType = ad.actionType || 'url';
    if (actionType === 'whatsapp') {
      return buildWhatsappUrl({
        phone: ad.phone || ad.whatsappNumber || ad.contactPhone || ad.advertiser?.phone,
        title: ad.title,
        customMessage: ad.whatsappMessage,
      });
    }
    if (actionType === 'internal') {
      return ad.internalPath || ad.actionValue || '/ads-center';
    }
    return ad.linkUrl || ad.actionValue || null;
  };

  const handleAdClick = async (event) => {
    event.preventDefault();
    await trackEvent('click');
    const target = resolveTarget();
    if (!target) return;
    if ((ad.actionType || 'url') === 'internal') {
      navigate(target);
      return;
    }
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  // Efecto para registrar la impresión cuando el anuncio es visible
  useEffect(() => {
    // 🟢 SOLUCIÓN: Solo observar si el anuncio ya tiene un ID
    if (!ad._id) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackEvent('impression');
          observer.disconnect(); // Solo registrar la impresión una vez
        }
      },
      { threshold: 0.5 } // Se considera visible si el 50% del anuncio está en pantalla
    );

    if (adRef.current) observer.observe(adRef.current);

    return () => observer.disconnect();
  }, [ad._id]);

  return (
    <div ref={adRef} className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden group transition-colors duration-300">
      {ad.imageUrl && (
        <div className="relative overflow-hidden">
          <a href={resolveTarget() || '#'} className="block" onClick={handleAdClick}>
            <MediaPlaceholder
              src={ad.imageUrl}
              alt={ad.title}
              fallbackAssets={fallbackAssets}
              className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </a>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
          {badgeLabel && <span className="uppercase tracking-wide">{badgeLabel}</span>}
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
          <a href={resolveTarget() || '#'} className="hover:text-blue-600 dark:hover:text-blue-400" onClick={handleAdClick}>
            {ad.title}
          </a>
        </h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm mb-2 line-clamp-2">
          {ad.content}
        </p>
      </div>
    </div>
  );
};

export default AdCard;